import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  QrCode, ArrowLeft, Download, Share2, RefreshCw, 
  Home, Calendar, Clock, User, UserPlus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { accessApi } from '../services/accessApi';
// Walk-in "Sin QR" (roadmap item E, 2026-09-16): guard-role users can register
// a visitor without a QR from this page too. Guard contexts only — the button
// renders exclusively for scanner roles.
import WalkInRegisterModal from '../components/scan/WalkInRegisterModal';

export default function QrScannerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'my-qr' | 'visitors'>('my-qr');
  // Roadmap item E: "Visitante sin QR" — guard contexts only (scanner roles).
  const SCANNER_ROLES = ['admin', 'sub_admin', 'seguridad', 'security', 'super_admin', 'directive'];
  const isScannerRole = SCANNER_ROLES.includes(String(user?.role || ''));
  const [walkInModalOpen, setWalkInModalOpen] = useState(false);

  // BUG FIX (2026-08-21): was fully mocked. Load the user's real access code
  // from GET /api/qr-access/my-codes so the guard can validate it via
  // GET /api/qr-access/validate/:code.
  const [myCode, setMyCode] = useState<string | null>(null);
  useEffect(() => {
    accessApi.qrAccess.myCodes()
      .then((res) => {
        const list = res.data?.data?.codes || res.data?.codes || [];
        const personal = list.find((c: any) => c.type === 'resident') || list[0];
        if (personal?.code) setMyCode(personal.code);
      })
      .catch(() => { /* keep placeholder */ });
  }, []);
  const qrData = myCode || (user?.id ? `VECINDUS:${user.id}` : 'VECINDUS:GUEST');

  const handleDownload = () => {
    // In production, this would generate and download a QR code image
    alert('Descarga de QR en desarrollo');
  };

  const handleShare = () => {
    // In production, this would share the QR code
    if (navigator.share) {
      navigator.share({
        title: 'Mi Código QR Vecindus',
        text: `Código de acceso para ${user?.name || 'residente'}`,
        url: window.location.href,
      });
    } else {
      alert('Compartir no disponible en este navegador');
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/home/dashboard')} 
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Mi Acceso</h1>
              <p className="text-xs text-gray-500">Código QR personal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#36CCD8] to-[#2DB8C4] rounded-full flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.[0] || 'U'}
            </div>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-md mx-auto">
        {/* Walk-in "Sin QR" (roadmap item E): guard-role quick action. Hidden
            for residents/employees (guard contexts only). */}
        {isScannerRole && (
          <button
            type="button"
            onClick={() => setWalkInModalOpen(true)}
            className="w-full py-4 mb-6 bg-gray-800 border-2 border-[#36CCD8]/60 text-white font-bold rounded-2xl hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            <UserPlus className="w-5 h-5 text-[#36CCD8]" /> Visitante sin QR
          </button>
        )}

        {/* Welcome Section */}
        <div className="text-center mb-8">
          <p className="text-gray-400 text-sm">{getGreeting()}</p>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mt-1">{user?.name || 'Residente'}</h2>
          {user?.unit && (
            <p className="text-[#36CCD8] text-sm mt-1 flex items-center justify-center gap-1">
              <Home className="w-4 h-4" /> {user.unit}
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-800 rounded-2xl p-1 mb-6">
          <button
            onClick={() => setActiveTab('my-qr')}
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'my-qr' 
                ? 'bg-[#36CCD8] text-white shadow-lg' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Mi QR
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'visitors' 
                ? 'bg-[#36CCD8] text-white shadow-lg' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Registrar Visita
          </button>
        </div>

        {/* My QR Code Section */}
        {activeTab === 'my-qr' && (
          <>
            {/* QR Code Display */}
            <div className="relative bg-white rounded-3xl p-8 mb-6 shadow-2xl">
              {/* QR Code Pattern - stylized representation */}
              <div className="aspect-square bg-gray-100 rounded-2xl flex items-center justify-center relative overflow-hidden">
                {/* Mock QR pattern */}
                <div className="w-48 h-48 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-2">
                  <div className="w-full h-full bg-white rounded-lg flex items-center justify-center">
                    <div className="grid grid-cols-5 gap-1 p-2">
                      {/* Top-left corner */}
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full bg-gray-900 rounded-sm" />
                      </div>
                      <div className="col-span-3 row-span-1">
                        <div className="w-full h-full flex gap-1 p-0.5">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-2 h-2 bg-gray-900 rounded-sm" />
                          ))}
                        </div>
                      </div>
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full bg-gray-900 rounded-sm" />
                      </div>
                      {/* Middle section - mixed pattern */}
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full flex flex-col gap-1 p-0.5">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-2 h-2 bg-gray-900 rounded-sm" />
                          ))}
                        </div>
                      </div>
                      <div className="col-span-3 row-span-1">
                        <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                          <User className="w-12 h-12 text-gray-400" />
                        </div>
                      </div>
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full flex flex-col gap-1 p-0.5">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-2 h-2 bg-gray-900 rounded-sm" />
                          ))}
                        </div>
                      </div>
                      {/* Bottom corners */}
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full bg-gray-900 rounded-sm" />
                      </div>
                      <div className="col-span-3 row-span-1">
                        <div className="w-full h-full flex gap-1 p-0.5">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className="w-2 h-2 bg-gray-900 rounded-sm" />
                          ))}
                        </div>
                      </div>
                      <div className="col-span-1 row-span-1">
                        <div className="w-full h-full bg-gray-900 rounded-sm" />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Scan animation ring */}
                <div className="absolute inset-0 border-4 border-[#36CCD8]/30 rounded-2xl animate-pulse" />
              </div>
              
              {/* QR Info */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500 mb-1">Tu código de acceso</p>
                {myCode ? (
                  <p className="text-lg font-mono font-bold text-gray-800 tracking-widest">{myCode}</p>
                ) : (
                  <p className="text-xs text-gray-400">Genera tu código desde "Mis QR" si aún no tienes uno</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button 
                onClick={handleDownload}
                className="flex items-center justify-center gap-2 py-4 bg-gray-800 text-white rounded-2xl hover:bg-gray-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                <span className="font-medium">Descargar</span>
              </button>
              <button 
                onClick={handleShare}
                className="flex items-center justify-center gap-2 py-4 bg-gray-800 text-white rounded-2xl hover:bg-gray-700 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-medium">Compartir</span>
              </button>
            </div>

            {/* Info Card */}
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#36CCD8]" />
                ¿Cómo usar tu código?
              </h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#36CCD8] font-bold">1.</span>
                  Muestra este código QR al guardia de seguridad
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#36CCD8] font-bold">2.</span>
                  El guardia escaneará tu código con su dispositivo
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#36CCD8] font-bold">3.</span>
                  Recibirás confirmación de acceso en segundos
                </li>
              </ul>
            </div>
          </>
        )}

        {/* Register Visitor Section */}
        {activeTab === 'visitors' && (
          <div className="bg-gray-800 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#36CCD8]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-[#36CCD8]" />
              </div>
              <h3 className="text-white font-bold text-lg">Registrar Visitante</h3>
              <p className="text-gray-400 text-sm mt-1">Crea un código QR temporal para tu visitante</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Nombre del visitante</label>
                <input 
                  type="text" 
                  placeholder="Ej: Juan Pérez"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#36CCD8]"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Unidad a visitar</label>
                <input 
                  type="text" 
                  placeholder="Ej: Torre A, Apto 101"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#36CCD8]"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Fecha de visita</label>
                <input 
                  type="date" 
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-[#36CCD8]"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Hora de visita</label>
                <input 
                  type="time" 
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-[#36CCD8]"
                />
              </div>

              <button className="w-full py-4 bg-[#36CCD8] text-white font-bold rounded-2xl hover:bg-[#2DB8C4] flex items-center justify-center gap-2 mt-4">
                <QrCode className="w-5 h-5" />
                Generar Código QR
              </button>
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 text-xs">
            ¿Problems con el escaneo? Contacta a seguridad
          </p>
        </div>
      </main>
      {/* Walk-in "Sin QR" registration modal (roadmap item E) */}
      <WalkInRegisterModal
        open={walkInModalOpen}
        onClose={() => setWalkInModalOpen(false)}
        condoId={user?.condoId || undefined}
        onCreated={() => { /* the guard list lives on VerifyQR; creation refresh happens there */ }}
      />
    </div>
  );
}
